import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios, { AxiosRequestConfig } from 'axios';
import { getToken } from '../services/otpAuthService';
import axiosInstance from '../services/axiosInstance';
import { useAuth } from '../contexts/AuthContext';
import { PERMESSO_FORMALITA_STAFF } from '../components/FormalitaCanaliGate';
import { useToast } from '../contexts/ToastContext';
import {
  FiltroDl98,
  FormalitaCategoria,
  FormalitaData,
  FormalitaItem,
  RIF_CIRCOLARE,
  Segmento,
  filtraPerCategoria,
  isDl98Si,
  linkSegnalazione,
  segmenti,
  termini,
} from '../utils/formalitaCanali';
import '../styles/formalita-canali.css';

/**
 * Guida "Classificazione delle formalità e canali utilizzabili" (Circ. DGMoT n. 22022).
 * Solo associati loggati (AssociatePrivateRoute + API protetta), noindex, nessuna card:
 * si raggiunge solo dal link diretto /area-riservata/formalita-canali.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

type ChiaveFile = 'pdf' | 'docx' | 'circolare' | 'dm159' | 'dm159nota';
const NOMI_FILE: Record<'pdf' | 'docx', string> = {
  pdf: 'Classificazione Formalita UNASCA.pdf',
  docx: 'Classificazione Formalita UNASCA.docx',
};

const TAGS: [string, string][] = [
  ['sportello', 'Sportello'],
  ['sta', 'Stampa online STA'],
  ['prenota', 'Prenota · ritiro UMC'],
  ['dl98', 'DL98'],
  ['umc', 'Stampa UMC / tecnico'],
];

const DL_OPTS: [FiltroDl98, string][] = [['all', 'Tutte'], ['si', 'Sì'], ['no', 'No']];

const Evidenziato: React.FC<{ segs: Segmento[] }> = ({ segs }) => (
  <>
    {segs.map((s, i) => (s.hit ? <mark key={i} className="fc-mark">{s.t}</mark> : <span key={i}>{s.t}</span>))}
  </>
);

const IconaDownload = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
);

const scrollaA = (id: string) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
};

const FormalitaCanaliPage: React.FC = () => {
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  // Staff (sessione admin con permesso): usa axiosInstance e vede anche la versione Word
  const staff = !!user && hasPermission(PERMESSO_FORMALITA_STAFF);
  const apiGet = useCallback(
    <T,>(url: string, config: AxiosRequestConfig = {}) =>
      staff
        ? axiosInstance.get<T>(url, config)
        : axios.get<T>(`${API_BASE}${url}`, { ...config, headers: { Authorization: `Bearer ${getToken()}` } }),
    [staff],
  );
  const [data, setData] = useState<FormalitaData | null>(null);
  const [errore, setErrore] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [dl, setDl] = useState<FiltroDl98>('all');
  const [tags, setTags] = useState<string[]>([]);
  const [prem, setPrem] = useState(false);
  const [focus, setFocus] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [scaricando, setScaricando] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let attivo = true;
    apiGet<FormalitaData>('/formalita-canali')
      .then((r) => { if (attivo) setData(r.data); })
      .catch(() => { if (attivo) setErrore(true); });
    return () => { attivo = false; };
  }, [apiGet]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(t);
  }, [flash]);

  const apriFile = useCallback(async (key: ChiaveFile) => {
    // La finestra va aperta subito (gesto utente), altrimenti il popup blocker la ferma.
    const finestra = key in NOMI_FILE ? null : window.open('', '_blank');
    setScaricando(key);
    try {
      const r = await apiGet<Blob>(`/formalita-canali/file/${key}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (finestra) {
        finestra.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = NOMI_FILE[key as keyof typeof NOMI_FILE];
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      finestra?.close();
      toast.error('Download non riuscito', 'Riprova tra qualche istante.');
    } finally {
      setScaricando(null);
    }
  }, [apiGet, toast]);

  const cats: FormalitaCategoria[] = useMemo(() => data?.categorie || [], [data]);
  const terms = useMemo(() => termini(q), [q]);
  const perCat = useMemo(() => filtraPerCategoria(cats, { terms, dl, tags }), [cats, terms, dl, tags]);
  const visibili = perCat.filter((x) => (cat === 'all' || x.c.id === cat) && x.items.length);
  const totale = visibili.reduce((a, x) => a + x.items.length, 0);
  const nTot = cats.reduce((a, c) => a + c.items.length, 0);
  const matchAll = perCat.reduce((a, x) => a + x.items.length, 0);
  const hasFilters = !!q || cat !== 'all' || dl !== 'all' || tags.length > 0;

  const risultati = visibili.flatMap((x) => x.items.map((it) => ({ c: x.c, it })));
  const anteprima = risultati.slice(0, 6);
  const showPreview = !!data && focus && terms.length > 0;
  const anchorDi = (c: FormalitaCategoria, it: FormalitaItem) => `f-${c.num}-${it.n}`;
  // Note (es. D.M. 159/2026) mostrate subito dopo la categoria che contiene le formalità con asterisco
  const noteDelGruppo = (items: FormalitaItem[]) =>
    (data?.noteFinali || []).filter((n) => items.some((it) => it.nota === n.id));

  const vaiAllElenco = () => {
    setFocus(false);
    searchRef.current?.blur();
    scrollaA('elenco');
  };
  const vaiA = (c: FormalitaCategoria, it: FormalitaItem) => {
    setFocus(false);
    searchRef.current?.blur();
    scrollaA(anchorDi(c, it));
    setFlash(anchorDi(c, it));
  };
  const resetAll = () => { setQ(''); setCat('all'); setDl('all'); setTags([]); };

  const premessaVis = data ? (prem ? data.premessa : data.premessa.slice(0, 1)) : [];
  // Nel design i box sono in ordine inverso rispetto al documento (regola generale prima)
  const boxes = data ? [data.boxes[1], data.boxes[0]].filter(Boolean) : [];

  const renderParagrafo = (p: string) =>
    p.split(RIF_CIRCOLARE).map((parte, i) =>
      i % 2 === 1 ? (
        <button key={i} type="button" className="fc-link" onClick={() => apriFile('circolare')} disabled={scaricando === 'circolare'} title="Apri la circolare (PDF)">
          {parte}
        </button>
      ) : (
        <React.Fragment key={i}>{parte}</React.Fragment>
      ),
    );

  const labelAltri = risultati.length > anteprima.length
    ? `Vedi tutti i ${risultati.length} risultati →`
    : risultati.length === 1 ? 'Vai al risultato nell’elenco →' : `Vai ai ${risultati.length} risultati nell’elenco →`;

  return (
    <div className="fc-page">
      <Helmet>
        <title>Formalità e canali utilizzabili - UNASCA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="fc-hero">
        <div className="fc-hero-glow" />
        <div className="fc-wrap fc-hero-inner">
          <div className="fc-hero-top">
            <span className="fc-kicker">Guida operativa · Circ. DGMoT n. 22022</span>
            <div className="fc-hero-actions">
              {staff && (
                <button type="button" className="fc-btn-light" onClick={() => apriFile('docx')} disabled={scaricando === 'docx'} title="Solo staff: versione modificabile">
                  <IconaDownload />
                  {scaricando === 'docx' ? 'Download…' : 'Word (staff)'}
                </button>
              )}
              <button type="button" className="fc-btn-light" onClick={() => apriFile('pdf')} disabled={scaricando === 'pdf'}>
                <IconaDownload />
                {scaricando === 'pdf' ? 'Download…' : 'Scarica PDF'}
              </button>
            </div>
          </div>
          <h1 className="fc-title">Classificazione delle formalità e canali utilizzabili</h1>
          <p className="fc-subtitle">Guida operativa per gli sportelli e gli studi di consulenza automobilistica</p>

          <div className="fc-search-box">
            <label className="fc-search">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#057839" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input
                ref={searchRef}
                type="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setFocus(true); }}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); vaiAllElenco(); }
                  else if (e.key === 'Escape') setFocus(false);
                }}
                placeholder="Cerca una formalità, un codice o una parola chiave…"
                aria-label="Cerca una formalità"
              />
              {q && (
                <button type="button" className="fc-search-clear" aria-label="Cancella ricerca" onClick={(e) => { e.preventDefault(); setQ(''); searchRef.current?.focus(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              )}
              <span className="fc-search-key">/</span>
            </label>
            {showPreview && (
              <div className="fc-preview" onMouseDown={(e) => e.preventDefault()}>
                {risultati.length === 0 && <div className="fc-preview-empty">Nessuna formalità corrisponde alla ricerca.</div>}
                <div className="fc-preview-list">
                  {anteprima.map(({ c, it }) => (
                    <button key={anchorDi(c, it)} type="button" className="fc-preview-item" onClick={() => vaiA(c, it)}>
                      <span className="fc-code">{`${c.num}.${it.n}`}</span>
                      <span className="fc-preview-text">
                        <span className="fc-preview-title"><Evidenziato segs={segmenti(it.formalita, terms)} /></span>
                        <span className="fc-preview-meta">{`${c.nome} · DL98: ${it.dl98}`}</span>
                      </span>
                    </button>
                  ))}
                </div>
                {risultati.length > 0 && (
                  <button type="button" className="fc-preview-more" onClick={vaiAllElenco}>{labelAltri}</button>
                )}
              </div>
            )}
          </div>

          <div className="fc-stats">
            <div className="fc-stat"><span className="fc-stat-num">{cats.length || '—'}</span><span className="fc-mono">Categorie</span></div>
            <div className="fc-stat"><span className="fc-stat-num">{nTot || '—'}</span><span className="fc-mono">Formalità</span></div>
            <div className="fc-stat"><span className="fc-stat-num"><small>versione </small>3.0</span><span className="fc-mono">Aggiornata il 30/09/2026</span></div>
          </div>
        </div>
      </section>

      {errore ? (
        <div className="fc-state">Impossibile caricare la guida. Ricarica la pagina o riprova più tardi.</div>
      ) : (
        <>
          <section className="fc-wrap fc-intro">
            <div className="fc-card-prem">
              <span className="fc-kicker">Premessa</span>
              {premessaVis.map((p, i) => <p key={i}>{renderParagrafo(p)}</p>)}
              {data && (
                <button type="button" className="fc-toggle" onClick={() => setPrem(!prem)}>
                  {prem ? 'Mostra meno' : 'Leggi la premessa completa'}
                </button>
              )}
            </div>
            <div className="fc-boxes">
              {boxes.map((b) => (
                <details key={b.title} className="fc-box">
                  <summary>
                    <span>{b.title}</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>
                  </summary>
                  <div className="fc-box-body">
                    {b.body.map((t, i) => <p key={i}>{t}</p>)}
                  </div>
                </details>
              ))}
            </div>
          </section>

          <main id="elenco" className="fc-wrap fc-main">
            <aside className="fc-aside">
              <div className="fc-filter">
                <span className="fc-mono">Obbligo DL98</span>
                <div className="fc-seg">
                  {DL_OPTS.map(([v, label]) => (
                    <button key={v} type="button" className={dl === v ? 'is-active' : ''} onClick={() => setDl(v)}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="fc-filter">
                <span className="fc-mono">Canale utilizzabile</span>
                <div className="fc-chips">
                  {TAGS.map(([v, label]) => {
                    const attivo = tags.includes(v);
                    return (
                      <button key={v} type="button" className={`fc-chip${attivo ? ' is-active' : ''}`} onClick={() => setTags(attivo ? tags.filter((t) => t !== v) : [...tags, v])}>
                        {attivo && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <nav className="fc-cats">
                <span className="fc-mono">Categoria</span>
                {[{ id: 'all', num: '', label: 'Tutte le categorie', count: matchAll }, ...perCat.map((x) => ({ id: x.c.id, num: String(x.c.num), label: x.c.nome, count: x.items.length }))].map((o) => (
                  <button key={o.id} type="button" className={`fc-cat${cat === o.id ? ' is-active' : ''}`} onClick={() => { setCat(o.id); scrollaA('elenco'); }}>
                    <span className="fc-cat-label">{o.num && <span className="fc-cat-num">{o.num}</span>}{o.label}</span><span>{o.count}</span>
                  </button>
                ))}
              </nav>
            </aside>

            <div className="fc-list">
              <div className="fc-list-head">
                <div>
                  <span className="fc-kicker">Elenco delle formalità per categoria</span>
                  <span className="fc-count">{!data ? 'Caricamento…' : hasFilters ? `${totale} di ${nTot} formalità` : `${nTot} formalità`}</span>
                </div>
                {hasFilters && <button type="button" className="fc-btn-ghost" onClick={resetAll}>Azzera filtri</button>}
              </div>

              {!!data && totale === 0 && (
                <div className="fc-empty">
                  <strong>Nessuna formalità trovata</strong>
                  <span>Prova con un termine diverso o azzera i filtri attivi.</span>
                </div>
              )}

              {visibili.map(({ c, items }) => (
                <section key={c.id} id={c.id} className="fc-group">
                  <div className="fc-group-head">
                    <span className="fc-group-num">{String(c.num).padStart(2, '0')}</span>
                    <h2 className="fc-group-title">{c.nome}</h2>
                    <span className="fc-mono">{items.length === 1 ? '1 formalità' : `${items.length} formalità`}</span>
                  </div>
                  {items.map((it) => {
                    const si = isDl98Si(it);
                    const anchor = anchorDi(c, it);
                    return (
                      <article key={anchor} id={anchor} className={`fc-item${flash === anchor ? ' is-flash' : ''}`}>
                        <div className="fc-item-main">
                          <div className="fc-item-tags">
                            <span className="fc-code">{`${c.num}.${it.n}`}</span>
                            <span className={`fc-badge ${si ? 'fc-badge-si' : 'fc-badge-no'}`}>Obbligo DL98: {si ? 'Sì' : 'No'}</span>
                          </div>
                          <h3 className="fc-item-title">
                            <Evidenziato segs={segmenti(it.formalita, terms)} />
                            {it.nota && (
                              <a href={`#nota-${it.nota}`} className="fc-asterisco" title="Vedi la nota in fondo all'elenco" onClick={(e) => { e.preventDefault(); scrollaA(`nota-${it.nota}`); }}>*</a>
                            )}
                          </h3>
                          <div className="fc-item-canale">
                            <span className="fc-mono">Canale utilizzabile</span>
                            <span><Evidenziato segs={segmenti(it.canale, terms)} /></span>
                          </div>
                        </div>
                        <div className="fc-item-note">
                          <span className="fc-mono">Note operative</span>
                          <p><Evidenziato segs={segmenti(it.note, terms)} /></p>
                          <Link to={linkSegnalazione(`${c.num}.${it.n}`, it.formalita)} className="fc-segnala">
                            Qualcosa non torna? Segnalalo
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                {noteDelGruppo(items).length > 0 && (
                  <section className="fc-note-finali" aria-label="Note">
                    {noteDelGruppo(items).map((n) => (
                      <div key={n.id} id={`nota-${n.id}`} className="fc-nota">
                        <strong><span className="fc-asterisco">*</span> {n.titolo}</strong>
                        {n.testo.map((t, i) => <p key={i}>{t}</p>)}
                        {n.allegati.length > 0 && (
                          <div className="fc-nota-allegati">
                            {n.allegati.map((a) => (
                              <button key={a.file} type="button" className="fc-link" onClick={() => apriFile(a.file as ChiaveFile)} disabled={scaricando === a.file}>
                                {a.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </section>
                )}
                </section>
              ))}
            </div>
          </main>

          <section className="fc-wrap fc-contribuisci-wrap">
            <div className="fc-contribuisci">
              <div className="fc-contribuisci-text">
                <span className="fc-kicker">Una guida che cresce con voi</span>
                <h2 className="fc-contribuisci-title">Avete trovato un errore o avete un’esperienza diversa?</h2>
                <p>
                  Questa tabella nasce dall’osservazione quotidiana degli sportelli e degli studi di tutta Italia, e può
                  migliorare solo con il contributo di ciascuno di noi. Se una formalità è classificata in modo non corretto,
                  se un canale indicato non funziona come descritto o se manca un caso che affrontate ogni giorno, scriveteci:
                  ogni segnalazione viene letta e, se confermata, entra nella prossima versione.
                </p>
              </div>
              <Link to={linkSegnalazione()} className="fc-btn-primary">Invia una segnalazione</Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default FormalitaCanaliPage;
